import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';

import { TimeRangeInput } from 'dashboards/types/time';

import { USER_TIMEZONE } from 'services/timezone';

export type TimeRangeParams = TimeRangeInput & {
  preset: string;
  timezone: string;
};

export function adjustTimezoneAndKeepLocalTime({
  timezone,
  date,
}: {
  timezone: string;
  date: DateTime;
}) {
  return date.setZone(timezone, { keepLocalTime: true }).toISO();
}

export function useTimeRangeParams(): TimeRangeParams {
  const [params, setSearchParams] = useSearchParams();

  const preset = params.get('preset') || 'TODAY';
  const timezone = params.get('timezone') || USER_TIMEZONE;

  const defaultStart = adjustTimezoneAndKeepLocalTime({
    timezone,
    date: DateTime.now().startOf('day'),
  })!;
  const defaultStop = adjustTimezoneAndKeepLocalTime({
    timezone,
    date: DateTime.now().endOf('day'),
  })!;

  const start = params.get('start') || defaultStart;
  const stop = params.get('stop') || defaultStop;

  // Validate that stop time is not before start time
  const startDateTime = DateTime.fromISO(start);
  const stopDateTime = DateTime.fromISO(stop);

  if (
    !startDateTime.isValid ||
    !stopDateTime.isValid ||
    startDateTime > stopDateTime
  ) {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set('preset', 'TODAY');
      newParams.set('start', defaultStart);
      newParams.set('stop', defaultStop);
      newParams.set('timezone', timezone);
      return newParams;
    });

    return {
      preset: 'TODAY',
      start: defaultStart,
      stop: defaultStop,
      timezone,
    };
  }

  return {
    preset,
    start,
    stop,
    timezone,
  };
}
